import React, { JSX } from "react";
import { cn } from "@/lib/utils";
import { typography as typographyStyles } from "@/lib/theme";

type TypographyVariant = keyof typeof typographyStyles;
type ElementType = keyof JSX.IntrinsicElements;

export interface TypographyProps {
  variant?: TypographyVariant;
  as?: ElementType;
  className?: string;
  children?: React.ReactNode;
}

const defaultElements: Record<TypographyVariant, ElementType> = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  p: "p",
  blockquote: "blockquote",
  lead: "p",
  large: "p",
  small: "p",
  muted: "p",
};

/**
 * Typography component for consistent text styling across the application.
 * Uses the design system typography presets.
 *
 * Note: The "p" variant includes automatic top margin for non-first paragraphs
 * ([&:not(:first-child)]:mt-6) to create proper content flow. If you need
 * paragraphs without this spacing, use the Text component or override with className.
 *
 * @example
 * <Typography variant="h1">Large Heading</Typography>
 * <Typography variant="lead">This is a lead paragraph</Typography>
 * <Typography variant="muted" as="span">Muted inline text</Typography>
 */
export function Typography({
  variant = "p",
  as,
  className,
  children,
  ...props
}: TypographyProps & React.HTMLAttributes<HTMLElement>) {
  const Component = as ?? defaultElements[variant];

  return React.createElement(
    Component,
    {
      className: cn(typographyStyles[variant], className),
      ...props,
    },
    children,
  );
}

// Convenience components for common typography variants
export const H1 = (
  props: Omit<TypographyProps, "variant"> & React.HTMLAttributes<HTMLHeadingElement>,
) => <Typography variant="h1" {...props} />;

export const H2 = (
  props: Omit<TypographyProps, "variant"> & React.HTMLAttributes<HTMLHeadingElement>,
) => <Typography variant="h2" {...props} />;

export const H3 = (
  props: Omit<TypographyProps, "variant"> & React.HTMLAttributes<HTMLHeadingElement>,
) => <Typography variant="h3" {...props} />;

export const H4 = (
  props: Omit<TypographyProps, "variant"> & React.HTMLAttributes<HTMLHeadingElement>,
) => <Typography variant="h4" {...props} />;

export const P = (
  props: Omit<TypographyProps, "variant"> & React.HTMLAttributes<HTMLParagraphElement>,
) => <Typography variant="p" {...props} />;

export const Lead = (
  props: Omit<TypographyProps, "variant"> & React.HTMLAttributes<HTMLParagraphElement>,
) => <Typography variant="lead" {...props} />;

export const Large = (
  props: Omit<TypographyProps, "variant"> & React.HTMLAttributes<HTMLParagraphElement>,
) => <Typography variant="large" {...props} />;

export const Small = (
  props: Omit<TypographyProps, "variant"> & React.HTMLAttributes<HTMLParagraphElement>,
) => <Typography variant="small" {...props} />;

export const Muted = (
  props: Omit<TypographyProps, "variant"> & React.HTMLAttributes<HTMLParagraphElement>,
) => <Typography variant="muted" {...props} />;

export const Blockquote = (
  props: Omit<TypographyProps, "variant"> & React.HTMLAttributes<HTMLQuoteElement>,
) => <Typography variant="blockquote" as="blockquote" {...props} />;
